/**
 * AddLinkDialog Component
 * Dialog form for adding new links with react-hook-form + zod validation
 */

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Effect } from "effect";
import { toast } from "sonner";
import type { CreateLinkRequest } from "@link-storage/contracts";
import { createLink } from "../services/linkService";
import { createLinkSchema, type CreateLinkInput } from "../schemas/link";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { TagInput } from "./TagInput";

interface AddLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddLinkDialog({
  open,
  onOpenChange,
  onSuccess,
}: AddLinkDialogProps) {
  const form = useForm<CreateLinkInput>({
    resolver: zodResolver(createLinkSchema),
    defaultValues: {
      url: "",
      title: "",
      tags: [],
    },
  });

  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = async (data: CreateLinkInput) => {
    const request: CreateLinkRequest = {
      url: data.url.trim(),
      title: data.title?.trim() || undefined,
      tags: data.tags && data.tags.length > 0 ? data.tags : undefined,
    };

    const program = createLink(request).pipe(
      Effect.tap(() =>
        Effect.sync(() => {
          toast.success("링크가 저장되었습니다", {
            description: request.url,
          });
          form.reset();
          onOpenChange(false);
          onSuccess?.();
        })
      ),
      Effect.tapError((error) =>
        Effect.sync(() => {
          const message =
            error._tag === "ValidationError"
              ? error.message
              : error._tag === "DatabaseError" || error._tag === "UnknownError"
                ? "네트워크 오류가 발생했습니다"
                : "링크 저장 중 오류가 발생했습니다";
          toast.error("저장 실패", { description: message });
        })
      ),
      Effect.catchAll(() => Effect.void)
    );

    await Effect.runPromise(program);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>새 링크 추가</DialogTitle>
          <DialogDescription>
            저장할 링크의 URL을 입력하세요. 제목은 자동으로 가져옵니다.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    URL <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://example.com"
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>제목 (선택)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="링크 제목을 입력하세요"
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    비워두면 페이지에서 자동으로 가져옵니다
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>태그 (선택)</FormLabel>
                  <FormControl>
                    <TagInput
                      value={field.value || []}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "저장 중..." : "저장하기"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
